/**
 * Renders and updates the cart popup notification with product details and subtotal price.
 *
 * @param {Object} params - Parameters for rendering the popup.
 * @param {Object} params.productInfo - Product information to display in the popup.
 * @param {number} params.items_subtotal_price - Current subtotal price of the cart.
 *
 * @returns {void}
 */
const renderCartPopupNotification = ({ productInfo, items_subtotal_price }) => {
  const SYMBOL = document.querySelector("body").getAttribute("data-currency-symbol");
  const popupElement = getContainerPopup();
  const popupContainer = popupElement.querySelector('.cart-popup-container');

  //Change image
  const imgEl = popupContainer.querySelector('.cart-popup-product-image');
  let imgSrc = '';
  if (productInfo.featured_image) {
    imgSrc = productInfo.featured_image.url.replace(/(\.[^.]*)$/, "_400x400$1");
    imgEl.alt = productInfo.featured_image.alt;
  } else if (productInfo.properties && productInfo.properties._Thumbnail) {
    imgSrc = productInfo.properties._Thumbnail.replace(/(\.[^.]*)$/, "_400x400$1");
    imgEl.alt = '';
  }
  imgEl.src = imgSrc;

  //Change title
  const titleEl = popupContainer.querySelector('.cart-popup-product-title');
  if (productInfo.properties?.Purpose) {
    titleEl.innerHTML = `
      <span class="heading-24 md:text-20">Remedy</span>
      <span class="text-script text-32 mx-1.5 ml-2 relative top-0.5 md:text-22 md_min:ml-3">for</span>
      <span>${productInfo.properties.Purpose}</span>
    `;
  } else {
    titleEl.innerHTML = `
      <span class="heading-18 md:text-18">Remedy</span>
      <span class="inline-block text-script text-22 ml-2 mr-1">for</span>
      <span class="text-18">${productInfo.product_title.replace(/^(Remedy for |for )/i, '')}</span>
    `;
  }

  //Change options variant and subscription
  const optionsEl = popupContainer.querySelector('.cart-popup-product-options');
  if (!productInfo.product_has_only_default_variant && productInfo.options_with_values.length > 0) {
    optionsEl.innerHTML = productInfo.options_with_values
      .map(e => `<p class="text-18 font-heading md:text-14">${e.value}</p>`)
      .join("");
  } else {
    optionsEl.innerHTML = '';
  }

  const sellingPlanEl = popupContainer.querySelector('.cart-popup-product-selling-plan');
  sellingPlanEl.innerHTML = productInfo.selling_plan_allocation?.selling_plan?.name || '';

  // Change update price
  const finalPriceEl = popupContainer.querySelector('.final-price');
  finalPriceEl.textContent = productInfo.final_line_price === 0 
    ? 'FREE'
    : Shopify.formatMoney(productInfo.final_line_price).replace("$", SYMBOL);

  const originalPriceEl = popupContainer.querySelector('.original-price');
  if (productInfo.final_line_price === 0 || productInfo.total_discount === 0) {
    originalPriceEl.style.display = 'none';
  } else {
    originalPriceEl.textContent = Shopify.formatMoney(productInfo.original_line_price).replace("$", SYMBOL).split('.')[0];
    originalPriceEl.style.display = 'inline';
  }

  // Subtotal
  const subtotalEl = popupContainer.querySelector('.cart-popup-subtotal');
  subtotalEl.textContent = Shopify.formatMoney(items_subtotal_price).replace("$", SYMBOL);

  popupElement.classList.add('active');
};

/**
 * Retrieves the cart popup notification container element.
 *
 * @returns {HTMLElement|null} The cart popup element if found, otherwise null.
 */
function getContainerPopup() {
  return document.getElementById('cart-popup-notification');
}

/**
 * Handles the "Keep Shopping" action by hiding the cart popup.
 *
 * @returns {boolean} Always returns false to prevent default link behavior.
 */
function handleKeepShopping() {
  getContainerPopup().classList.remove('active');
  return false;
};

/**
 * Handles the "View Cart" action by hiding the cart popup and showing the AJAX cart.
 *
 * @returns {boolean} Always returns false to prevent default link behavior.
 */
function handleViewCart() {
  getContainerPopup().classList.remove('active');
  document.body.classList.add('js-show-ajax-cart');
  return false;
};

/**
 * Extracts and returns product and cart information from an add-to-cart event.
 *
 * @param {CustomEvent} event - The custom event containing request state data.
 * @param {Object} event.detail.requestState - The request state object.
 * @param {Object} event.detail.requestState.responseData - Response data for the added product.
 * @param {Object} event.detail.requestState.extraResponseData - Additional response data containing cart info.
 *
 * @returns {{productInfo: Object, items_subtotal_price: number}} An object with the added product info and cart subtotal price.
 */
function getAddedProductInfo(event) {
  const { requestState } = event.detail;
  const { responseData, extraResponseData } = requestState;

  const dataProductAdded = responseData.body;
  const dataCart = extraResponseData.body;

  return {
    productInfo: dataProductAdded,
    items_subtotal_price: dataCart.items_subtotal_price
  } 
}

/**
 * Renders the cart gamification bar inside the popup by copying its content from the main cart gamification element.
 *
 * @returns {void}
 */
function renderBarGamificationOnPopup() { 
  const cartGamification = document.querySelector('.cart-gamification-js');
  const cartGamificationPopup = document.querySelector('.cart-gamification-popup-js');
  if (cartGamification && cartGamificationPopup) {
    cartGamificationPopup.innerHTML = cartGamification.innerHTML;
  }
}

/**
 * Event listener for the end of a Liquid Ajax Cart request.
 * 
 * When an "add to cart" request completes successfully and the AJAX cart is not shown,
 * it renders the cart popup notification and the gamification bar inside the popup.
 * It also attaches event handlers for the "Keep Shopping" and "View Cart" buttons.
 *
 * @event liquid-ajax-cart:request-end
 * @param {CustomEvent} event - The event containing request state details.
 * @param {Object} event.detail.requestState - The state of the Ajax cart request.
 * @param {string} event.detail.requestState.requestType - The type of request (e.g., "add").
 * @param {Object} event.detail.requestState.responseData - Response data from the request.
 *
 * @returns {void}
 */
document.addEventListener('liquid-ajax-cart:request-end', event => {
  const { requestState } = event.detail;

  if (requestState.requestType === 'add' && requestState.responseData?.ok && !document.querySelector('body.js-show-ajax-cart')) {
    const productInfo = getAddedProductInfo(event);
    renderCartPopupNotification(productInfo);
    renderBarGamificationOnPopup();
  }

  document.querySelectorAll('.cart-popup-keep-shopping').forEach(element => {
    element.addEventListener('click', handleKeepShopping);
  });
  document.querySelector('.cart-popup-view-cart').addEventListener('click', handleViewCart);
});